import React from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer, use$ } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { characters$ } from '../utils/Supa';
import { Tables } from '../utils/database.types';
import { appState$, isCharacterUnlocked, unlockCharacter, spendCoins, isSubscribed, isVip } from '../utils/AppState';
import AdSenseBanner from '../components/AdSenseBanner';

const CharacterCard = observer(
  ({
    character,
    onPress,
    onUnlock,
  }: {
    character: Tables<'characters'>;
    onPress: () => void;
    onUnlock: () => void;
  }) => {
    const isUnlocked = isCharacterUnlocked(character.id);
    const locked = character.is_premium && !isUnlocked;

    return (
      <TouchableOpacity
        onPress={() => {
          if (locked) {
            onUnlock();
          } else {
            onPress();
          }
        }}
        style={styles.card}
        activeOpacity={0.7}
      >
        {character.avatar_url ? (
          <Image source={{ uri: character.avatar_url }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: character.accent_color }]}>
            <Text style={styles.avatarText}>{character.name[0]}</Text>
          </View>
        )}

        {locked ? (
          <View style={styles.lockBadge}>
            <Ionicons name="lock-closed" size={14} color="#F6F7FF" />
          </View>
        ) : null}

        <View style={styles.cardOverlay}>
          <Text style={styles.name}>{character.name}</Text>
          <Text style={styles.description} numberOfLines={2}>
            {character.description ?? 'Talk and have fun with your AI companion'}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }
);

const UNLOCK_COST = 50;

const HomeScreen = observer(({ onCharacterSelect, onPlayGames, onCreateCharacter }: { onCharacterSelect: (character: Tables<'characters'>) => void; onPlayGames: () => void; onCreateCharacter?: () => void }) => {
  const insets = useSafeAreaInsets();
  const characters = characters$.get();
  const streak = use$(appState$.streak);
  const userName = use$(appState$.userName);

  const handleUnlock = (character: Tables<'characters'>) => {
    Alert.alert(
      'Unlock Character',
      `Unlock ${character.name} for ${UNLOCK_COST} coins?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unlock',
          onPress: () => {
            if (spendCoins(UNLOCK_COST)) {
              unlockCharacter(character.id);
            } else {
              Alert.alert('Not Enough Coins', `You need ${UNLOCK_COST} coins to unlock this character.`);
            }
          },
        },
      ]
    );
  };


  return (
    <View style={styles.container}>
      <View style={{ height: Math.max(insets.top, 10) }} />

      <View style={styles.headerRow}>
        <View style={{ flexShrink: 1 }}>
          <Text style={styles.userName}>Hello, {userName ?? 'Guest'} 👋</Text>
          <Text style={styles.header}>Welcome Back Babe</Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 6 }}>
          {isVip() && (
            <View style={styles.vipBadge}>
              <Ionicons name="star" size={12} color="#FFD700" />
              <Text style={styles.vipText}>VIP</Text>
            </View>
          )}
          <View style={styles.streakBadge}>
            <Text style={styles.streakEmoji}>🔥</Text>
            <View>
              <Text style={styles.streakValue}>{streak}</Text>
              <Text style={styles.streakLabel}>day streak</Text>
            </View>
          </View>
        </View>
      </View>

      <TouchableOpacity
        style={styles.promoCard}
        activeOpacity={0.85}
        onPress={onPlayGames}
      >
        <View style={styles.promoIconWrap}>
          <Ionicons name="game-controller" size={25} color="#FFF3FF" />
        </View>
        <View style={styles.promoTextWrap}>
          <Text style={styles.promoTitle}>Play Games & Earn Coins</Text>
          <Text style={styles.promoSubtitle}>Word Connect, Puzzles & More</Text>
        </View>
        <Ionicons name="chevron-forward" size={24} color="#F6D8FF" />
      </TouchableOpacity>

      <ScrollView style={styles.listWrap} contentContainerStyle={styles.grid} showsVerticalScrollIndicator={false}>
        {isSubscribed() && (
          <TouchableOpacity
            style={styles.createCharacterCard}
            onPress={() => onCreateCharacter?.()}
            activeOpacity={0.85}
          >
            <Ionicons name="add-circle" size={32} color="#B748FF" />
            <Text style={styles.createCharacterTitle}>Create Character</Text>
            <Text style={styles.createCharacterSub}>Design your own AI companion</Text>
          </TouchableOpacity>
        )}
        {(characters ? Object.values(characters) : []).map((item) => (
          <View key={item.id} style={styles.gridItem}>
            <CharacterCard
              character={item}
              onPress={() => onCharacterSelect(item)}
              onUnlock={() => handleUnlock(item)}
            />
          </View>
        ))}
        {Platform.OS === 'web' && <AdSenseBanner />}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1F',
    paddingHorizontal: 12,
  },
  headerRow: {
    marginTop: 10,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  userName: {
    color: '#BFC4E0',
    fontSize: 14,
    marginBottom: 4,
  },
  header: {
    fontSize: Platform.OS === 'web' ? 41 : 32,
    fontWeight: '700',
    color: '#FF2CA8',
    lineHeight: Platform.OS === 'web' ? 43 : 36,
    flexShrink: 1,
  },
  streakBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: 'rgba(255, 61, 174, 0.5)',
    backgroundColor: 'rgba(255, 45, 168, 0.12)',
    borderRadius: 30,
    paddingHorizontal: 13,
    paddingVertical: 8,
    gap: 8,
    shadowColor: '#FF2CA8',
    shadowOpacity: 0.35,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  },
  streakEmoji: {
    fontSize: 21,
  },
  streakValue: {
    color: '#F6F7FF',
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 24,
  },
  streakLabel: {
    color: '#C7CBE8',
    fontSize: 11,
  },
  promoCard: {
    marginBottom: 18,
    borderRadius: 22,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0228F',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    shadowColor: '#F0228F',
    shadowOpacity: 0.4,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 6 },
  },
  promoIconWrap: {
    width: 50,
    height: 50,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.26)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  promoTextWrap: {
    flex: 1,
  },
  promoTitle: {
    color: '#FFF3FF',
    fontSize: 26,
    fontWeight: '700',
  },
  promoSubtitle: {
    marginTop: 3,
    color: '#FFDCF4',
    fontSize: 19,
    fontWeight: '500',
  },
  listWrap: {
    flex: 1,
    width: '100%',
    ...(Platform.OS === 'web' ? { maxWidth: 960, alignSelf: 'center' } : {}),
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingBottom: 12,
    gap: 12,
  },
  gridItem: {
    width: '48%',
  },
  card: {
    backgroundColor: '#171B33',
    borderRadius: 22,
    width: '100%',
    aspectRatio: 0.85,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 52,
    fontWeight: '700',
    color: '#F6F7FF',
  },
  lockBadge: {
    position: 'absolute',
    top: 11,
    right: 11,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 12,
    paddingTop: 18,
    paddingBottom: 10,
    backgroundColor: 'rgba(8,11,31,0.46)',
  },
  name: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  description: {
    fontSize: 12,
    color: '#E9EAFB',
    lineHeight: 16,
  },
  vipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255,215,0,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255,215,0,0.5)',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  vipText: {
    color: '#FFD700',
    fontSize: 11,
    fontWeight: '700',
  },
  createCharacterCard: {
    width: '48%',
    aspectRatio: 0.85,
    backgroundColor: '#171B33',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(183,72,255,0.4)',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  createCharacterTitle: {
    color: '#B748FF',
    fontSize: 15,
    fontWeight: '700',
  },
  createCharacterSub: {
    color: '#8D93B3',
    fontSize: 12,
    textAlign: 'center',
    paddingHorizontal: 12,
  },
});

export default HomeScreen;
